/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    generatePath, RouteObject,
} from 'react-router-dom'
import {
    SkyNetLoaderInitFunction,
} from 'app/requests/skyNetQueryLoader/skyNetQueryLoader'
import {
    RolesTypes,
} from 'app/utils/hasPermission'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    APPS, SkyNetNavigateFunction,
    SkyNetGeneratePathParams, RoutesConfig, SkyNetRoutesConfig,
    NavigateFunctionArgs, RoutesDescription,
    RouteDescription, NavigateFunction, RouteParamsConfig,
    SkyNetRouteConfigObject, SkyNetRouteObject,
} from './SkyNetRoutes.types'

export const createSearchParam = (params: Record<string, string>) => {
    return `?${String(new URLSearchParams(Object.entries(params)))}`
}

export const convertSkyNetPathToLink = ({
    module,
    domain,
    domainPath,
    absolute,
}: Omit<SkyNetGeneratePathParams, 'params' | 'searchParams'>) => {
    const init = absolute ? CommonRoutes.Slash.concat(APPS, CommonRoutes.Slash) : ''

    return init.concat([
        module,
        domain,
        ...domainPath,
    ].filter(Boolean).join('/'))
}

export const generateSkyNetLink = ({
    module,
    domain,
    domainPath,
    params,
    searchParams,
    absolute = true,
}: SkyNetGeneratePathParams) => {
    let fullPath = convertSkyNetPathToLink({
        absolute,
        module,
        domain,
        domainPath,
    })

    if (searchParams) {
        fullPath = fullPath.concat(createSearchParam(searchParams))
    }

    return generatePath(fullPath, params)
}

const createRouteStringParam = ({
    name, required,
}) => {
    return `:${name}${required ? '' : '?'}`
}

export function createRoutesConfig(skyNetNavigate: SkyNetNavigateFunction, options: {
    absolute: boolean
} = {
    absolute: true,
}) {
    return function parseRouteConfig(
        config: SkyNetRoutesConfig | RoutesDescription,
    ): RoutesConfig {
        const {
            path,
            via,
            params,
            routeParams,
            requiredParams,
            stringParams,
            route,
            navigate,
            pattern,
            ...routes
        }: {
            path?: string[],
            via?: RoutesConfig,
            params?: RouteParamsConfig[],
            requiredParams?: string[],
            routeParams?: string[],
            stringParams?: string,
            navigate?: NavigateFunction,
            route?: string,
            pattern?: string,
            routes?: Record<string, RoutesConfig | RouteDescription>,
        } = config

        return (Object.entries(routes) || []).reduce((acc, [
            key,
            value,
        ]) => {
            const paramNames = ('params' in value) && (value.params || []).map(createRouteStringParam)
            const mergedRouteParams = ('params' in value) ? [
                ...(routeParams || []) as string[],
                ...paramNames,
            ] : routeParams

            const mergedPath = [
                ...(path || []),
                ...(value.via?.path || []),
                value.route,
                ...(paramNames || []),
            ] as string[]

            const mergedRequiredParams: string[] = [
                ...value?.requiredParams || [],
                ...value?.via?.requiredParams || [],
                ...(value?.params || []).map(({
                    name, required,
                }) => {
                    if (required) {
                        return name
                    }
                    return null
                }).filter(Boolean),
            ]

            return {
                ...acc,
                [key]: {
                    requiredParams: mergedRequiredParams as string[],
                    route: value.route,
                    params: value.params,
                    routeParams: mergedRouteParams as string[],
                    stringParams: ('params' in value) && paramNames?.join(CommonRoutes.Slash),
                    // eslint-disable-next-line func-names
                    navigate: value.navigate || function (navigateParams?: NavigateFunctionArgs) {
                        try {
                            const errors = mergedRequiredParams.reduce((errorsAcc, param) => {
                                if (!Object.keys(navigateParams.params).includes(param)) {
                                    errorsAcc.push(`Param "${param}" is required to specify`)
                                }
                                return errorsAcc
                            }, [] as string[])

                            if (errors.length) {
                                throw Error(errors.join('\n'))
                            }

                            skyNetNavigate(mergedPath, navigateParams)
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.error('Unable to call navigate:', e)
                        }
                    },
                    path: mergedPath,
                    pattern: convertSkyNetPathToLink({
                        domainPath: mergedPath,
                        absolute: options.absolute,
                    }),
                    ...parseRouteConfig({
                        ...value,
                        path: mergedPath,
                    } as RoutesDescription),
                },
            } as RoutesConfig
        }, {} as RoutesConfig)
    }
}

export function createSkyNetRoutes({
    routesConfig = {},
    routes = [],
}: {
    routesConfig?: SkyNetRouteConfigObject<any>;
    routes?: RouteObject[];
}) {
    return [
        ...Object.entries(routesConfig).map(([
            path,
            element,
        ]) => {
            const resultRoute = {
                path: `${path}/${CommonRoutes.Asterisk}`,
            }

            if ('element' in element) {
                return {
                    ...element,
                    ...resultRoute,
                }
            }
            return {
                ...resultRoute,
                element,
            }
        }),
        ...routes,
    ] as unknown as RouteObject[]
}

export const injectSkyNetConfigParams = (
    routes: SkyNetRouteObject[],
    configParams: {
        loader: SkyNetLoaderInitFunction,
        hasPermission: (roles: RolesTypes[]) => boolean,
    },
): RouteObject[] => {
    const {
        loader, hasPermission,
    } = configParams

    return routes.reduce((acc, route) => {
        if (route.accessOnly && !hasPermission(route.accessOnly)) {
            return acc
        }
        const newRoute = {
            ...route,
        }

        if (newRoute.skyNetQueryLoaderConfig) {
            newRoute.loader = newRoute.loader || loader(newRoute.skyNetQueryLoaderConfig)
        }

        if (newRoute.children) {
            newRoute.children = injectSkyNetConfigParams(newRoute.children, configParams)
        }

        return [
            ...acc,
            newRoute,
        ]
    }, [])
}
